import React, { useState, useEffect } from 'react';
import apiClient from '../../../lib/api';
import { ShieldCheck, Plus, Edit, Trash2, Building, X, AlertTriangle, Star } from 'lucide-react';
import toast from 'react-hot-toast';

function formatMinutes(mins?: number) {
  if (!mins) return '1 hour';
  if (mins < 60) return `${mins} mins`;
  const hrs = Math.round((mins / 60) * 10) / 10;
  return `${hrs} ${hrs === 1 ? 'hour' : 'hours'}`;
}

const INITIAL_CLIENTS_DATA: Record<string, any> = {
  'Default SLA Policy': {
    tier: 'Standard Default SLA',
    contractId: 'SLA-DEFAULT-STD',
    coveredAssets: 'All Unassigned & Standard Clients',
    isDefault: true,
    policies: [
      { id: 101, priority: 'Urgent / Critical', firstResponse: '30 mins', resolution: '4 hours', escalationNotify: true, targetCompliance: '98.0%' },
      { id: 102, priority: 'High Priority', firstResponse: '1 hour', resolution: '8 hours', escalationNotify: true, targetCompliance: '95.0%' },
      { id: 103, priority: 'Medium Priority', firstResponse: '4 hours', resolution: '24 hours', escalationNotify: false, targetCompliance: '92.0%' },
      { id: 104, priority: 'Low Priority', firstResponse: '8 hours', resolution: '48 hours', escalationNotify: false, targetCompliance: '90.0%' },
    ],
  },
};

export function SLATrackingView() {
  const [clientsMap, setClientsMap] = useState<Record<string, any>>(INITIAL_CLIENTS_DATA);
  const [selectedClient, setSelectedClient] = useState('Default SLA Policy');

  const clientInfo = clientsMap[selectedClient] || clientsMap['Default SLA Policy'] || Object.values(clientsMap)[0] || { tier: '', contractId: '', coveredAssets: '', policies: [] };
  const [policies, setPolicies] = useState<any[]>(clientInfo.policies || []);
  useEffect(() => {
    async function loadDbPolicies() {
      try {
        const res = await apiClient.get('/support/sla/policies');
        const rawList = res.data?.data || res.data;
        if (Array.isArray(rawList) && rawList.length > 0) {
          const newMap: Record<string, any> = {};
          let defName = '';
          rawList.forEach((pol: any) => {
            const polTargets = (pol.targets || []).map((t: any) => ({
              id: t.id,
              priority: t.priorityName || t.priorityCode || 'Standard',
              firstResponse: formatMinutes(t.responseTargetMinutes),
              resolution: formatMinutes(t.resolutionTargetMinutes),
              escalationNotify: Boolean(t.escalationWarningPercent),
              targetCompliance: `${t.escalationWarningPercent || 95}.0%`,
            }));

            if (polTargets.length === 0) {
              polTargets.push(
                { id: 1, priority: 'Urgent / Critical', firstResponse: '30 mins', resolution: '4 hours', escalationNotify: true, targetCompliance: '98.0%' },
                { id: 2, priority: 'High Priority', firstResponse: '1 hour', resolution: '8 hours', escalationNotify: true, targetCompliance: '95.0%' },
                { id: 3, priority: 'Medium Priority', firstResponse: '4 hours', resolution: '24 hours', escalationNotify: false, targetCompliance: '92.0%' },
                { id: 4, priority: 'Low Priority', firstResponse: '8 hours', resolution: '48 hours', escalationNotify: false, targetCompliance: '90.0%' }
              );
            }

            newMap[pol.name] = {
              tier: pol.name,
              contractId: `SLA-${String(pol.id).padStart(3, '0')}`,
              coveredAssets: pol.description || 'Database Configured Policy',
              isDefault: Boolean(pol.isDefault),
              policies: polTargets,
            };

            if (pol.isDefault && !defName) {
              defName = pol.name;
            }
          });

          setClientsMap(newMap);
          const activeName = defName || rawList[0]?.name || 'Standard ITSM Service SLA';
          setSelectedClient(activeName);
          setPolicies(newMap[activeName]?.policies || []);
        }
      } catch (err) {
        console.warn('Failed to load DB SLA policies in SLATrackingView:', err);
      }
    }
    loadDbPolicies();
  }, []);


  // Modals
  const [showAddPolicyModal, setShowAddPolicyModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<any | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; id: number; priority: string }>({
    isOpen: false,
    id: 0,
    priority: '',
  });

  // Forms
  const [policyForm, setPolicyForm] = useState({
    priority: 'Urgent / Critical',
    firstResponse: '',
    resolution: '',
    escalationNotify: true,
    targetCompliance: '',
  });

  const [clientForm, setClientForm] = useState({
    name: '',
    tier: 'Enterprise VIP Tier',
    contractId: '',
    coveredAssets: '',
    isDefault: false,
  });

  const handleClientChange = (clientName: string) => {
    setSelectedClient(clientName);
    setPolicies(clientsMap[clientName]?.policies || []);
  };

  const handleSetAsDefaultSla = (clientName: string) => {
    setClientsMap(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        updated[key] = {
          ...updated[key],
          isDefault: key === clientName,
        };
      });
      return updated;
    });
    toast.success(`"${clientName}" set as the Primary System Default SLA!`);
  };

  const handleCreateClient = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientForm.name.trim()) {
      toast.error('Please enter a client name');
      return;
    }

    const name = clientForm.name.trim();
    const newClientObj = {
      tier: clientForm.tier,
      contractId: clientForm.contractId || `SLA-${name.substring(0, 4).toUpperCase()}-2026`,
      coveredAssets: clientForm.coveredAssets ? `${clientForm.coveredAssets} Hardware Assets` : 'Assigned Assets',
      isDefault: clientForm.isDefault,
      policies: [
        { id: Date.now(), priority: 'Urgent / Critical', firstResponse: '15 mins', resolution: '2 hours', escalationNotify: true, targetCompliance: '99.0%' },
        { id: Date.now() + 1, priority: 'High Priority', firstResponse: '1 hour', resolution: '4 hours', escalationNotify: true, targetCompliance: '98.0%' },
      ],
    };

    setClientsMap(prev => {
      const updated = { ...prev };
      if (clientForm.isDefault) {
        Object.keys(updated).forEach(k => {
          updated[k] = { ...updated[k], isDefault: false };
        });
      }
      updated[name] = newClientObj;
      return updated;
    });

    setSelectedClient(name);
    setPolicies(newClientObj.policies);
    toast.success(`Client "${name}" added successfully with default SLA targets!`);
    setShowAddClientModal(false);
    setClientForm({ name: '', tier: 'Enterprise VIP Tier', contractId: '', coveredAssets: '', isDefault: false });
  };

  const handleConfirmDeleteClient = () => {
    if (!selectedClient || Object.keys(clientsMap).length <= 1) {
      toast.error('Cannot delete the last remaining SLA configuration');
      return;
    }
    const targetClient = selectedClient;
    const remaining = { ...clientsMap };
    delete remaining[targetClient];
    const nextClientName = Object.keys(remaining)[0] || '';
    setClientsMap(remaining);
    setShowDeleteClientModal(false);
    if (nextClientName) {
      setSelectedClient(nextClientName);
      setPolicies(remaining[nextClientName]?.policies || []);
    } else {
      setSelectedClient('');
      setPolicies([]);
    }
    toast.success(`SLA configuration for "${targetClient}" deleted successfully!`);
  };

  const handleCreatePolicy = (e: React.FormEvent) => {
    e.preventDefault();
    const newP = { id: Date.now(), ...policyForm };
    const updatedPolicies = [...policies, newP];
    setPolicies(updatedPolicies);

    setClientsMap(prev => ({
      ...prev,
      [selectedClient]: {
        ...prev[selectedClient],
        policies: updatedPolicies,
      },
    }));

    toast.success(`SLA Policy for ${policyForm.priority} added to ${selectedClient}!`);
    setShowAddPolicyModal(false);
  };

  const handleUpdatePolicy = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPolicy) return;
    const updatedPolicies = policies.map((p) => (p.id === editingPolicy.id ? editingPolicy : p));
    setPolicies(updatedPolicies);

    setClientsMap(prev => ({
      ...prev,
      [selectedClient]: {
        ...prev[selectedClient],
        policies: updatedPolicies,
      },
    }));

    toast.success(`SLA Policy updated for ${editingPolicy.priority}!`);
    setEditingPolicy(null);
  };

  const handleDeletePolicy = (id: number, priority: string) => {
    setDeleteConfirm({ isOpen: true, id, priority });
  };

  const confirmDeletePolicy = () => {
    const updatedPolicies = policies.filter((p) => p.id !== deleteConfirm.id);
    setPolicies(updatedPolicies);

    setClientsMap(prev => ({
      ...prev,
      [selectedClient]: {
        ...prev[selectedClient],
        policies: updatedPolicies,
      },
    }));

    toast.success(`SLA Policy "${deleteConfirm.priority}" removed!`);
    setDeleteConfirm({ isOpen: false, id: 0, priority: '' });
  };

  return (
    <div className="space-y-4 text-foreground text-sm">
      {/* TOOLBAR */}
      <div className="bg-card border border-border p-3.5 rounded-xl shadow-xs mb-4 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
        {/* CLIENT SELECTOR, DELETE CLIENT & ADD CLIENT BUTTON */}
        <div className="flex items-center gap-3 flex-wrap w-full justify-between">
          <div className="flex items-center gap-2">
            <Building className="size-4 text-muted-foreground" />
            <span className="font-semibold text-xs text-foreground">Select Client / Policy:</span>
            <select
              value={selectedClient}
              onChange={(e) => handleClientChange(e.target.value)}
              className="h-8 rounded-md border border-input bg-card px-3 text-xs font-semibold text-foreground outline-none focus:ring-1 focus:ring-ring shadow-xs"
            >
              {Object.keys(clientsMap).map((cName) => (
                <option key={cName} value={cName}>
                  {clientsMap[cName].isDefault ? '★ ' : ''}{cName} ({clientsMap[cName].tier})
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setShowDeleteClientModal(true)}
            disabled={Object.keys(clientsMap).length <= 1}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-destructive/20 bg-destructive/10 px-3 text-xs font-semibold text-destructive hover:bg-destructive/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete selected SLA policy configuration"
          >
            <Trash2 className="size-3.5" /> Delete Client
          </button>

          <button
            onClick={() => setShowAddClientModal(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-semibold text-primary-foreground shadow-xs hover:bg-primary/90 transition-colors cursor-pointer"
          >
            <Plus className="size-3.5" /> Add Client
          </button>
        </div>
      </div>

      {/* DETAILED CLIENT SLA BANNER */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-xs grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border gap-4">
        <div className="md:pr-4 space-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block">Client / SLA Profile</span>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <span className="text-sm font-semibold text-foreground">{selectedClient}</span>
            {clientInfo.isDefault && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold text-xs border border-primary/20">
                <Star className="size-3 fill-primary" /> Default System SLA
              </span>
            )}
          </div>
          <span className="text-xs text-primary font-medium block">{clientInfo.tier}</span>
        </div>

        <div className="pt-3 md:pt-0 md:px-4 space-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block">Contract Reference</span>
          <p className="text-xs font-mono font-semibold text-foreground mt-0.5">{clientInfo.contractId}</p>
          <span className="text-xs text-muted-foreground block">Active SLA Contract</span>
        </div>

        <div className="pt-3 md:pt-0 md:pl-4 flex flex-col justify-between space-y-2">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block">Covered Client Assets</span>
            <p className="text-xs font-semibold text-foreground mt-0.5">{clientInfo.coveredAssets}</p>
            <span className="text-xs text-success-strong font-medium flex items-center gap-1.5 mt-0.5">
              <span className="size-1.5 rounded-full bg-success inline-block" /> Active Support Coverage
            </span>
          </div>
          {!clientInfo.isDefault && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => handleSetAsDefaultSla(selectedClient)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-secondary hover:bg-secondary/80 text-xs font-semibold text-foreground transition-colors"
              >
                <Star className="size-3 text-warning" /> Set as Default SLA
              </button>
            </div>
          )}
        </div>
      </div>

      {/* SLA POLICIES TABLE */}
      <div className="rounded-xl border border-border bg-card shadow-xs overflow-hidden">
        <div className="p-3.5 border-b border-border bg-card flex items-center justify-between">
          <h3 className="text-foreground font-display text-card-title">
            SLA Targets for {selectedClient} ({policies.length})
          </h3>

          <button
            onClick={() => setShowAddPolicyModal(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-semibold text-primary-foreground shadow-xs hover:bg-primary/90 cursor-pointer"
          >
            <Plus className="size-3.5" /> Add SLA Policy
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="border-b border-border bg-secondary/40 text-xs font-semibold text-muted-foreground">
              <tr className="h-9">
                <th className="py-2 px-4 font-medium">Priority Level</th>
                <th className="py-2 px-4 font-medium">First Response Target</th>
                <th className="py-2 px-4 font-medium">Resolution Target</th>
                <th className="py-2 px-4 font-medium">Breach Risk Notification</th>
                <th className="py-2 px-4 text-center font-medium">Target Compliance</th>
                <th className="py-2 px-4 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {policies.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-muted-foreground">
                    No SLA policies configured for {selectedClient}. Click "+ Add SLA Policy" above to add one.
                  </td>
                </tr>
              ) : (
                policies.map((pol) => (
                  <tr key={pol.id} className="h-10 hover:bg-muted/40 transition-colors">
                    <td className="py-2.5 px-4 font-semibold text-foreground">{pol.priority}</td>
                    <td className="py-2.5 px-4">
                      <span className="font-mono text-xs font-semibold text-foreground bg-secondary px-2 py-0.5 rounded border border-border/60">
                        {pol.firstResponse}
                      </span>
                    </td>
                    <td className="py-2.5 px-4">
                      <span className="font-mono text-xs font-semibold text-foreground bg-secondary px-2 py-0.5 rounded border border-border/60">
                        {pol.resolution}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-muted-foreground">
                      {pol.escalationNotify ? 'Yes (Email & Slack)' : 'Standard Alert'}
                    </td>
                    <td className="py-2.5 px-4 text-center">
                      <span className="font-mono text-xs font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded">
                        {pol.targetCompliance}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-right space-x-1">
                      <button
                        onClick={() => setEditingPolicy(pol)}
                        className="inline-flex h-7 items-center justify-center rounded-md border border-border bg-card px-2 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors shadow-2xs"
                        title="Edit Policy"
                      >
                        <Edit className="size-3" />
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(pol.id, pol.priority)}
                        className="inline-flex h-7 items-center justify-center rounded-md border border-border bg-card px-2 text-xs font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20 transition-colors shadow-2xs"
                        title="Remove Policy"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ADD CLIENT DRAWER */}
      {showAddClientModal && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-card-title text-foreground">Add New Client &amp; SLA Contract</h3>
              <button onClick={() => setShowAddClientModal(false)} className="text-muted-foreground hover:text-foreground cursor-pointer">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleCreateClient} className="flex-1 flex flex-col justify-between overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
                <div>
                  <label className="font-semibold text-foreground">Client Organization Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Nexus Global"
                    value={clientForm.name}
                    onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="font-semibold text-foreground">SLA Tier</label>
                    <select
                      value={clientForm.tier}
                      onChange={(e) => setClientForm({ ...clientForm, tier: e.target.value })}
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="Enterprise VIP Tier">Enterprise VIP Tier</option>
                      <option value="Premium Business Tier">Premium Business Tier</option>
                      <option value="Standard Support Tier">Standard Support Tier</option>
                      <option value="Mission-Critical SLA Tier">Mission-Critical SLA Tier</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-semibold text-foreground">Covered Assets Count</label>
                    <input
                      type="number"
                      value={clientForm.coveredAssets}
                      onChange={(e) => setClientForm({ ...clientForm, coveredAssets: e.target.value })}
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-foreground">Contract Reference ID</label>
                  <input
                    type="text"
                    placeholder="e.g. SLA-NEXUS-2026"
                    value={clientForm.contractId}
                    onChange={(e) => setClientForm({ ...clientForm, contractId: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="isDefaultCheck"
                    checked={clientForm.isDefault}
                    onChange={(e) => setClientForm({ ...clientForm, isDefault: e.target.checked })}
                    className="rounded border-input text-foreground focus:ring-0 cursor-pointer"
                  />
                  <label htmlFor="isDefaultCheck" className="text-xs font-semibold text-foreground cursor-pointer">
                    Set as Primary System Default SLA Policy
                  </label>
                </div>
              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddClientModal(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 shadow-xs cursor-pointer"
                >
                  Create SLA Contract
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD POLICY DRAWER */}
      {showAddPolicyModal && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-card-title text-foreground">Add SLA Policy Target for {selectedClient}</h3>
              <button onClick={() => setShowAddPolicyModal(false)} className="text-muted-foreground hover:text-foreground cursor-pointer">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleCreatePolicy} className="flex-1 flex flex-col justify-between overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
                <div>
                  <label className="font-semibold text-foreground">Priority Level *</label>
                  <select
                    value={policyForm.priority}
                    onChange={(e) => setPolicyForm({ ...policyForm, priority: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="Urgent / Critical">Urgent / Critical</option>
                    <option value="High Priority">High Priority</option>
                    <option value="Medium Priority">Medium Priority</option>
                    <option value="Low Priority">Low Priority</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="font-semibold text-foreground">First Response Target</label>
                    <input
                      type="text"
                      value={policyForm.firstResponse}
                      onChange={(e) => setPolicyForm({ ...policyForm, firstResponse: e.target.value })}
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs font-mono text-foreground outline-none focus:ring-1 focus:ring-ring"
                      placeholder="e.g. 15 mins"
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-foreground">Resolution Target</label>
                    <input
                      type="text"
                      value={policyForm.resolution}
                      onChange={(e) => setPolicyForm({ ...policyForm, resolution: e.target.value })}
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs font-mono text-foreground outline-none focus:ring-1 focus:ring-ring"
                      placeholder="e.g. 2 hours"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="escalationNotifyCheck"
                    checked={policyForm.escalationNotify}
                    onChange={(e) => setPolicyForm({ ...policyForm, escalationNotify: e.target.checked })}
                    className="rounded border-input text-foreground focus:ring-0 cursor-pointer"
                  />
                  <label htmlFor="escalationNotifyCheck" className="text-xs font-semibold text-foreground cursor-pointer">
                    Send Email &amp; Slack alert on breach risk
                  </label>
                </div>
              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddPolicyModal(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 shadow-xs cursor-pointer"
                >
                  Save Policy
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT POLICY DRAWER */}
      {editingPolicy && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-card-title text-foreground">Edit SLA Policy Target</h3>
              <button onClick={() => setEditingPolicy(null)} className="text-muted-foreground hover:text-foreground cursor-pointer">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleUpdatePolicy} className="flex-1 flex flex-col justify-between overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
                <div>
                  <label className="font-semibold text-foreground">Priority Level</label>
                  <input
                    type="text"
                    readOnly
                    value={editingPolicy.priority}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-muted px-2.5 text-xs font-semibold text-foreground outline-none cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="font-semibold text-foreground">First Response Target</label>
                    <input
                      type="text"
                      value={editingPolicy.firstResponse}
                      onChange={(e) => setEditingPolicy({ ...editingPolicy, firstResponse: e.target.value })}
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs font-mono text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-foreground">Resolution Target</label>
                    <input
                      type="text"
                      value={editingPolicy.resolution}
                      onChange={(e) => setEditingPolicy({ ...editingPolicy, resolution: e.target.value })}
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs font-mono text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="editEscalationNotify"
                    checked={editingPolicy.escalationNotify}
                    onChange={(e) => setEditingPolicy({ ...editingPolicy, escalationNotify: e.target.checked })}
                    className="rounded border-input text-foreground focus:ring-0 cursor-pointer"
                  />
                  <label htmlFor="editEscalationNotify" className="text-xs font-semibold text-foreground cursor-pointer">
                    Send Email &amp; Slack alert on breach risk
                  </label>
                </div>
              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingPolicy(null)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 shadow-xs cursor-pointer"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE CLIENT DRAWER */}
      {showDeleteClientModal && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-sm bg-card border-l border-border h-full flex flex-col justify-between p-5 shadow-2xl space-y-4 animate-in slide-in-from-right duration-200">
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-rose-600">
                <AlertTriangle className="size-6 shrink-0" />
                <h3 className="font-display text-card-title text-foreground">Confirm Client Deletion</h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Are you sure you want to delete client organization <strong className="text-foreground font-semibold">{selectedClient}</strong> ({clientInfo?.tier}) and all <strong className="text-foreground font-semibold">{policies.length}</strong> associated SLA policies? Active support coverage for this client will be permanently removed.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-border">
              <button
                onClick={() => setShowDeleteClientModal(false)}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteClient}
                className="rounded-md bg-rose-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 shadow-xs cursor-pointer"
              >
                Confirm Delete Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DOUBLE CONFIRMATION DELETE DRAWER */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-sm bg-card border-l border-border h-full flex flex-col justify-between p-5 shadow-2xl space-y-4 animate-in slide-in-from-right duration-200">
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-destructive">
                <AlertTriangle className="size-6 shrink-0" />
                <h3 className="text-foreground font-display text-card-title">Confirm SLA Policy Removal</h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Are you sure you want to remove SLA Policy for <strong>"{deleteConfirm.priority}"</strong>?
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-border">
              <button
                onClick={() => setDeleteConfirm({ isOpen: false, id: 0, priority: '' })}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePolicy}
                className="rounded-md bg-destructive px-4 py-1.5 text-xs font-semibold text-white hover:bg-destructive/90 shadow-xs cursor-pointer"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
